/**
 * Password reset request. On success we show the same calm confirmation
 * regardless of whether the address exists — no account enumeration.
 */
import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Banner, Button, StatusCard, TextField } from '@/components/ui'
import { PATHS } from '@/app/router/paths'
import { isCloudConfigured } from '@/lib/supabase/client'
import { isAppError, toAppError } from '@/lib/supabase/errors'
import type { AppError } from '@/types'
import { useAuth } from '../useAuth'
import { validateEmail } from '../validation'
import { AuthLayout, AuthLegalFooter, NotConfiguredBanner, RustWordmark } from './AuthLayout'

export default function ForgotPasswordPage() {
  const { sendPasswordReset } = useAuth()
  const navigate = useNavigate()
  const configured = isCloudConfigured()

  const [email, setEmail] = useState('')
  const [fieldError, setFieldError] = useState<string | undefined>(undefined)
  const [formError, setFormError] = useState<AppError | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [sent, setSent] = useState(false)

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (submitting || !configured) return
    const emailError = validateEmail(email)
    setFieldError(emailError ?? undefined)
    if (emailError) return

    setSubmitting(true)
    setFormError(null)
    try {
      await sendPasswordReset(email)
      setSent(true)
    } catch (error) {
      setFormError(isAppError(error) ? error : toAppError(error, 'auth'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthLayout backTo={PATHS.auth.clientLogin} backLabel="Terug naar inloggen">
      <RustWordmark width={140} />
      <h1 className="auth-headline" style={{ fontSize: 26 }}>
        Wachtwoord vergeten
      </h1>
      <p className="auth-subtitle">
        Vul je e-mailadres in. Als er een account bij hoort, ontvang je een link om een nieuw
        wachtwoord te kiezen.
      </p>

      {sent ? (
        <div className="auth-card">
          <StatusCard
            status="success"
            title="Controleer je e-mail"
            body="Als dit e-mailadres bekend is, heb je zojuist een herstel-link ontvangen. De link is beperkt geldig."
            action={
              <Button variant="primary" fullWidth onClick={() => navigate(PATHS.role)}>
                Terug naar inloggen
              </Button>
            }
          />
        </div>
      ) : (
        <form className="auth-card" onSubmit={onSubmit} noValidate>
          {!configured && <NotConfiguredBanner />}
          {formError && (
            <Banner tone="error">
              {formError.userMessage}
            </Banner>
          )}
          <TextField
            label="E-mailadres"
            type="email"
            value={email}
            onChange={setEmail}
            error={fieldError}
            required
            autoComplete="email"
            placeholder="naam@voorbeeld.nl"
            disabled={!configured || submitting}
            onBlur={() => setFieldError(validateEmail(email) ?? undefined)}
          />
          <div className="auth-form-footer">
            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={submitting}
              disabled={!configured || submitting}
            >
              Stuur herstel-link
            </Button>
          </div>
        </form>
      )}

      <div className="auth-links">
        <Link to={PATHS.role}>Terug naar rolkeuze</Link>
      </div>
      <AuthLegalFooter />
    </AuthLayout>
  )
}
